import { type ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router";

import { PlaceholderRoutePage } from "./PlaceholderRoutePage";
import { flattenAppRoutes, getRouteByKey } from "./utils";

import type { AppRouteDefinition, AppRouteProps } from "./types";

function renderRouteElement(
  definition: AppRouteDefinition,
  fallbackPath: string,
): ReactElement {
  if (definition.element) {
    return definition.element;
  }

  if (definition.route.redirectTo) {
    return (
      <Navigate replace to={getRouteByKey(definition.route.redirectTo).path} />
    );
  }

  if (definition.route.placeholder) {
    return <PlaceholderRoutePage routeKey={definition.route.key} />;
  }

  return <Navigate replace to={fallbackPath} />;
}

function RenderRouteNodes(
  fallbackPath: string,
  routes: readonly AppRouteDefinition[],
) {
  return (
    <>
      <Route element={<Navigate replace to={fallbackPath} />} index />
      {flattenAppRoutes(routes).map((definition) => (
        <Route
          element={renderRouteElement(definition, fallbackPath)}
          key={definition.id ?? definition.route.key}
          path={definition.path ?? definition.route.path}
        />
      ))}
      <Route element={<Navigate replace to={fallbackPath} />} path="*" />
    </>
  );
}

export function AppRoute({
  fallbackPath,
  layoutElement,
  routes,
}: AppRouteProps) {
  const routeNodes = RenderRouteNodes(fallbackPath, routes);

  return (
    <Routes>
      {layoutElement ? (
        <Route element={layoutElement}>{routeNodes}</Route>
      ) : (
        routeNodes
      )}
    </Routes>
  );
}
